import React, { useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import {
  MdMyLocation,
  MdRefresh,
  MdAccessTime,
  MdInfo,
  MdSettings,
} from "react-icons/md";
import "./TMLocationHeader.scss";
import UnavailableLocationsModal from "../UnavailableLocationModal/UnavalableLocationsModal";

interface UserLocation {
  id: string;
  latitude?: number;
  longitude?: number;
  updatedOn?: string;
}

interface TMLocationMapHeaderProps {
  totalArrivals: number;
  unavailableUsers: UserLocation[];
  hasAdminLocation: boolean;
  lastUpdated?: string;
  onFocusAdmin: () => void;
  onRefresh: () => void;
  onToggleLocationManager: () => void;
  onShowUnavailable?: () => void;
  isLoading?: boolean;
  showLocationManager?: boolean;
}

const TMLocationMapHeader: React.FC<TMLocationMapHeaderProps> = ({
  totalArrivals,
  unavailableUsers,
  hasAdminLocation,
  lastUpdated,
  onFocusAdmin,
  onRefresh,
  onToggleLocationManager,
  onShowUnavailable,
  isLoading = false,
  showLocationManager = false,
}) => {
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);

  const handleShowUnavailable = () => {
    setShowUnavailableModal(true);
    onShowUnavailable?.();
  };

  const formatLastUpdated = (dateString?: string): string => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  return (
    <>
      <div className="tm-header">
        <div className="tm-header-wrapper">
          {/* Left: Title & Description */}
          <div className="tm-header-left">
            <div className="tm-title-row">
              <FaMapMarkerAlt className="tm-title-icon" />
              <h1 className="tm-title">Team Member Location Map</h1>
            </div>
          </div>

          <div className="tm-stats">
            <div
              style={{ display: "flex", flexDirection: "row", gap: ".1rem" }}
            >
              <div className="tm-last-updated">
                <MdAccessTime className="tm-clock-icon" />
                <span>{`${totalArrivals} Arriving TM's`}</span>
              </div>

              {lastUpdated && (
                <div className="tm-last-updated">
                  <MdAccessTime className="tm-clock-icon" />
                  <span>Updated on: {formatLastUpdated(lastUpdated)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Actions */}
          <div className="tm-header-right">
            <div className="tm-actions">
              <button
                className={`tm-btn tm-btn--secondary ${
                  showLocationManager ? "tm-btn--active" : ""
                }`}
                onClick={onToggleLocationManager}
                title={
                  hasAdminLocation
                    ? "Manage admin location"
                    : "Set admin location"
                }
              >
                <MdSettings className="tm-btn-icon" />
                <span>
                  {hasAdminLocation ? "Manage Location" : "Set Admin Location"}
                </span>
              </button>

              <button
                className="tm-btn tm-btn--secondary"
                onClick={onRefresh}
                disabled={isLoading}
                title="Refresh locations"
              >
                <MdRefresh
                  className={`tm-btn-icon ${isLoading ? "tm-rotating" : ""}`}
                />
                <span>Refresh</span>
              </button>

              <button
                className="tm-btn tm-btn--primary"
                style={{ background: "#c90754" }}
                onClick={handleShowUnavailable}
                title="View unavailable team members"
              >
                <span>{`${unavailableUsers.length} Unavailable TM's`}</span>
              </button>

              {hasAdminLocation && (
                <button
                  className="tm-btn tm-btn--primary"
                  onClick={onFocusAdmin}
                  title="Focus on admin location"
                >
                  <MdMyLocation className="tm-btn-icon" />
                  <span>Focus Admin</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showUnavailableModal && (
        <UnavailableLocationsModal
          users={unavailableUsers}
          onClose={() => setShowUnavailableModal(false)}
        />
      )}
    </>
  );
};

export default TMLocationMapHeader;
